import { useDebounce } from '@hooks/useDebounce';
import { AssigneeType } from '../jobOffer.types';
import { Avatar, Box, Checkbox, Chip, IconButton, Stack, styled, TextField, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import * as React from 'react';
import { Roles } from '@utils/roleUtils';
import { useGetAssignableUsers } from '../hooks/useGetAssignableUsers';

type Props = {
    placeholder?: string;
    onChange: (value: any) => void;
    value: any[];
    proId?: string;
};

interface NormalizedUser {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    color?: string;
    file?: { url: string } | null;
    assigneeId: string;
    assigneeType: AssigneeType;
    higherUpId?: string | null;
}

interface TreeNode {
    user: NormalizedUser;
    children: TreeNode[];
    depth: number;
}

const Listbox = styled('ul')(({ theme }) => ({
    width: '100%',
    margin: 0,
    padding: 0,
    zIndex: 1200,
    position: 'absolute',
    listStyle: 'none',
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    maxHeight: 340,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.main}`,
    boxShadow: theme.shadows[4],
}));

const TreeRowContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '6px 12px',
    cursor: 'pointer',
    borderRadius: 8,
    transition: 'background-color 0.15s',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

/** Build a forest (array of root TreeNodes) from a flat user list */
function buildTree(users: NormalizedUser[], proRootId?: string): TreeNode[] {
    const idMap = new Map<string, NormalizedUser>();
    users.forEach(u => idMap.set(u._id, u));

    const childrenMap = new Map<string, NormalizedUser[]>();
    const roots: NormalizedUser[] = [];

    users.forEach(u => {
        const parentId = u.higherUpId;
        if (!parentId || !idMap.has(parentId)) {
            roots.push(u);
        } else {
            const siblings = childrenMap.get(parentId) ?? [];
            siblings.push(u);
            childrenMap.set(parentId, siblings);
        }
    });

    function makeNode(user: NormalizedUser, depth: number): TreeNode {
        const children = (childrenMap.get(user._id) ?? []).map(c => makeNode(c, depth + 1));
        return { user, children, depth };
    }

    return roots.map(r => makeNode(r, 0));
}

/** Flatten tree to a depth-annotated list for rendering */
function flattenTree(nodes: TreeNode[]): Array<{ user: NormalizedUser; depth: number; hasChildren: boolean }> {
    const result: Array<{ user: NormalizedUser; depth: number; hasChildren: boolean }> = [];
    function traverse(node: TreeNode) {
        result.push({ user: node.user, depth: node.depth, hasChildren: node.children.length > 0 });
        node.children.forEach(traverse);
    }
    nodes.forEach(traverse);
    return result;
}

function getRoleLabel(role: string): string {
    switch (role) {
        case Roles.ADMIN_PRO: return 'Admin';
        case Roles.PRO:
        case Roles.COMPANY: return 'Super Admin';
        default: return 'Membre';
    }
}

function getRoleColor(role: string): 'primary' | 'secondary' | 'default' {
    switch (role) {
        case Roles.ADMIN_PRO: return 'primary';
        case Roles.PRO:
        case Roles.COMPANY: return 'default';
        default: return 'secondary';
    }
}

export default function SearchMembers({ onChange, placeholder, value, proId }: Props) {
    const theme = useTheme();
    const [open, setOpen] = React.useState(true);
    const [selected, setSelected] = React.useState<NormalizedUser[]>([]);
    const [query, setQuery] = React.useState('');
    const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());

    const matcher = React.useMemo(() => ({
        query,
        pagination: { offset: 0, limit: 1000 },
        assigneeTypes: [] as AssigneeType[],
    }), [query]);

    const { fetchUsers, fetchMoreUsers, data, loading } = useGetAssignableUsers({ proId });
    const meta = React.useMemo(() => data?.meta ?? {}, [data]);

    const list: NormalizedUser[] = React.useMemo(() => {
        const rawList: any[] = data?.docs ?? [];
        return rawList.map((item: any) => {
            if (item.assigneeType === AssigneeType.OrganizationUser && item.assigneeDetails) {
            return {
                _id: item.assigneeDetails._id,
                firstName: item.assigneeDetails.firstName ?? '',
                lastName: item.assigneeDetails.lastName ?? '',
                email: item.assigneeDetails.email ?? '',
                role: item.assigneeDetails.role ?? '',
                color: item.assigneeDetails.color,
                file: item.assigneeDetails.file ?? null,
                higherUpId: item.assigneeDetails.higherUpId ?? null,
                assigneeId: item._id,
                assigneeType: item.assigneeType,
            } as NormalizedUser;
        } else if (item.assigneeType === AssigneeType.Pro && item.assigneeDetails) {
            return {
                _id: item.assigneeDetails._id,
                firstName: item.assigneeDetails.bossFirstname || item.assigneeDetails.commercialName || '',
                lastName: item.assigneeDetails.bossLastname || '',
                email: item.assigneeDetails.email ?? '',
                role: item.assigneeDetails.role ?? Roles.PRO,
                color: theme.palette.primary.main,
                file: item.assigneeDetails.bossPhoto ? { url: item.assigneeDetails.bossPhoto } : null,
                higherUpId: null,
                assigneeId: item._id,
                assigneeType: item.assigneeType,
            } as NormalizedUser;
        }
        return item as NormalizedUser;
        });
    }, [data?.docs, theme.palette.primary.main]);

    // Build tree only when not searching (flat list while searching)
    const isSearching = query.trim().length > 0;

    const flatItems = React.useMemo(() => {
        if (isSearching) {
            return list.map(u => ({ user: u, depth: 0, hasChildren: false }));
        }
        const tree = buildTree(list);
        return flattenTree(tree);
    }, [list, isSearching]);

    // Filtered flat items respecting collapse state
    const visibleItems = React.useMemo(() => {
        if (isSearching) return flatItems;
        const collapsedIds = collapsed;
        const idMap = new Map(list.map(u => [u._id, u]));
        return flatItems.filter(({ user, depth }) => {
            if (depth === 0) return true;
            let pid: string | null | undefined = user.higherUpId;
            while (pid) {
                const parent = idMap.get(pid);
                if (!parent) break;
                if (collapsedIds.has(parent._id)) return false;
                pid = parent.higherUpId;
            }
            return true;
        });
    }, [flatItems, collapsed, isSearching, list]);

    const debounceFilter = useDebounce(matcher, 400);

    React.useEffect(() => {
        fetchUsers(debounceFilter);
        setOpen(true);
    }, [debounceFilter, fetchUsers]);

    React.useEffect(() => {
        if (value) setSelected(value);
    }, [value]);

    const listRef = React.useRef<HTMLUListElement>(null);

    React.useEffect(() => {
        const el = listRef.current;
        const handleScroll = () => {
            if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 10 && meta.hasNextPage) {
                fetchMoreUsers({
                    query,
                    pagination: { offset: meta.page * (meta.limit ?? 20), limit: 1000 },
                    assigneeTypes: [],
                });
            }
        };
        el?.addEventListener('scroll', handleScroll);
        return () => el?.removeEventListener('scroll', handleScroll);
    }, [listRef, fetchMoreUsers, meta, query]);

    const toggleSelect = React.useCallback((user: NormalizedUser) => {
        setSelected(prev => {
            const isSelected = prev.some(s => s.email === user.email);
            const next = isSelected
                ? prev.filter(s => s.email !== user.email)
                : [...prev.filter(s => s.email !== user.email), user];
            onChange(next);
            return next;
        });
    }, [onChange]);

    const toggleCollapse = React.useCallback((id: string) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    return (
        <Box position="relative">
            <TextField
                value={query}
                onChange={e => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                name="member-search"
                type="search"
                size="small"
                aria-label="Search members"
                placeholder={placeholder}
                variant="outlined"
                color="primary"
                fullWidth
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                InputProps={{
                    sx: {
                        borderRadius: '32px',
                        backgroundColor: theme.palette.background.default,
                        gap: '10px',
                    },
                    style: { padding: '6px 14px', paddingLeft: '24px' },
                    startAdornment: (
                        <i className="fi-br-search" style={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    ),
                    endAdornment: (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            {loading && <CircularProgress color="inherit" size={18} />}
                            {open && (
                                <IconButton size="small" onClick={() => setOpen(false)}>
                                    <i className="fi-br-check" style={{ fontSize: 16, color: theme.palette.primary.main }} />
                                </IconButton>
                            )}
                        </Stack>
                    ),
                }}
            />

            {open && (
                <Listbox ref={listRef}>
                    {loading && visibleItems.length === 0 && (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {!loading && visibleItems.length === 0 && (
                        <Box py={3} textAlign="center">
                            <Typography variant="body2" color="text.secondary">
                                Aucun résultat
                            </Typography>
                        </Box>
                    )}
                    {visibleItems.map(({ user, depth, hasChildren }) => {
                        const isSelected = selected.some(s => s.email === user.email);
                        const isNodeCollapsed = collapsed.has(user._id);
                        const isPro = user.assigneeType === AssigneeType.Pro;

                        return (
                            <li key={user.email} style={{ listStyle: 'none' }}>
                                <TreeRowContainer
                                    sx={{
                                        pl: `${12 + depth * 24}px`,
                                        backgroundColor: isSelected ? theme.palette.action.selected : 'inherit',
                                    }}
                                    onClick={() => toggleSelect(user)}
                                >
                                    {/* Tree connector lines */}
                                    {depth > 0 && (
                                        <Box
                                            sx={{
                                                width: 16,
                                                height: '100%',
                                                mr: 0.5,
                                                flexShrink: 0,
                                                position: 'relative',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    left: 8,
                                                    top: '50%',
                                                    width: 8,
                                                    height: 1,
                                                    backgroundColor: theme.palette.divider,
                                                },
                                            }}
                                        />
                                    )}

                                    {/* Collapse toggle for nodes with children */}
                                    {hasChildren && !isSearching ? (
                                        <Tooltip title={isNodeCollapsed ? 'Développer' : 'Réduire'}>
                                            <IconButton
                                                size="small"
                                                onClick={e => { e.stopPropagation(); toggleCollapse(user._id); }}
                                                sx={{ mr: 0.5, p: 0.25 }}
                                            >
                                                <i
                                                    className={isNodeCollapsed ? 'fi-br-angle-right' : 'fi-br-angle-down'}
                                                    style={{ fontSize: 12, color: theme.palette.text.secondary }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Box sx={{ width: 28, flexShrink: 0 }} />
                                    )}

                                    <Checkbox
                                        size="small"
                                        checked={isSelected}
                                        onClick={e => e.stopPropagation()}
                                        onChange={() => toggleSelect(user)}
                                        sx={{ p: 0.5, mr: 1 }}
                                    />

                                    <Avatar
                                        src={user.file?.url}
                                        sx={{
                                            bgcolor: user.color || theme.palette.primary.main,
                                            width: isPro ? 36 : 32,
                                            height: isPro ? 36 : 32,
                                            fontSize: isPro ? 16 : 14,
                                            mr: 1.5,
                                            flexShrink: 0,
                                            border: isPro ? `2px solid ${theme.palette.primary.main}` : 'none',
                                        }}
                                    >
                                        {user.firstName.charAt(0).toUpperCase()}
                                    </Avatar>

                                    <Box overflow="hidden" flex={1}>
                                        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                                            <Typography
                                                variant="body2"
                                                fontWeight={depth === 0 ? 600 : 400}
                                                noWrap
                                                sx={{ color: theme.palette.text.primary }}
                                            >
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={getRoleLabel(user.role)}
                                                color={getRoleColor(user.role)}
                                                sx={{ height: 18, fontSize: 10 }}
                                            />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {user.email}
                                        </Typography>
                                    </Box>
                                </TreeRowContainer>
                            </li>
                        );
                    })}
                </Listbox>
            )}
        </Box>
    );
}
